import * as moment from 'moment';
import { TIMELINE_PIPELINE } from './timeline.pipeline';
import { getTimeline } from './timeline.tasks';
import { TTimelineModel, TBoardView } from './timeline.types';
import { TDate } from '@monorepo/types';
import { render } from 'bwip-js';

export const TIMELINE_MODEL = {
  createTimelineModel: <T1, T2, T3>(
    boardView: TBoardView,
    items: T1[],
    transform: (d: T1, i: number) => TTimelineModel<T1, T2, T3>['entities'][number],
    hideLeftPanel?: boolean,
  ): TTimelineModel<T1, T2, T3> => {
    const strips = items.map((v, index) => transform(v, index));

    return TIMELINE_PIPELINE.sanatize({
      boardView,
      pivot: moment().format('YYYY-MM-DD') as TDate,
      timeline: [],
      months: [],
      weekends: [],
      endOfMonths: [],
      entities: [...strips],
      comptedEntities: {
        totalEntities: strips.map((v) => v.entities.length).reduce((a, b) => a + b, 0),
        totalGroupEntities: strips.length,
      },
      hideLeftPanel: hideLeftPanel,
    });
  },
  changeTimelineType: <T1, T2, T3>(
    type: TTimelineModel<T1, T2, T3>['boardView']['boardViewType'],
    cm: TTimelineModel<T1, T2, T3>,
  ): TTimelineModel<T1, T2, T3> => {
    return TIMELINE_PIPELINE.sanatize({
      ...cm,
      boardView: {
        boardViewType: type,
      },
    });
  },
  next: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    let nextMonth = moment(cm.pivot).add(cm.timeline.length, 'd').format('YYYY-MM-DD') as TDate;
    if (cm.boardView.boardViewType === 'WEEKLY') {
      nextMonth = moment(cm.pivot)
        .add(cm.timeline.length / 2, 'd')
        .format('YYYY-MM-DD') as TDate;
    }
    return TIMELINE_PIPELINE.sanatize({
      ...cm,
      pivot: nextMonth,
    });
  },
  prev: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    let prevMonth = moment(cm.pivot).subtract(cm.timeline.length, 'd').format('YYYY-MM-DD') as TDate;
    if (cm.boardView.boardViewType === 'WEEKLY') {
      prevMonth = moment(cm.pivot)
        .subtract(cm.timeline.length / 2, 'd')
        .format('YYYY-MM-DD') as TDate;
    }
    return TIMELINE_PIPELINE.sanatize({
      ...cm,
      pivot: prevMonth,
    });
  },
  resetToday: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    const nextMonth = moment().format('YYYY-MM-DD') as TDate;

    return TIMELINE_PIPELINE.sanatize({
      ...cm,
      timeline: getTimeline(nextMonth, cm.boardView),
      pivot: nextMonth,
    });
  },
  applyLineDisableState: <T1, T2, T3>(
    timelineModel: TTimelineModel<T1, T2, T3>,
    validLineSubjects: string[],
  ): TTimelineModel<T1, T2, T3> => {
    return TIMELINE_PIPELINE.sanatize({
      ...timelineModel,
      entities: timelineModel.entities.map((entityGroup) => ({
        ...entityGroup,
        entities: entityGroup.entities.map((entity) => ({
          ...entity,
          disabled: !validLineSubjects.includes(entity.id),
        })),
      })),
    });
  },
};
